import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Language flags mapping
 * Using emoji flags for simplicity (works cross-platform)
 */
const LANGUAGE_FLAGS = {
  en: '🇬🇧',
  de: '🇩🇪',
  fr: '🇫🇷',
  it: '🇮🇹',
  es: '🇪🇸',
  hu: '🇭🇺',
  fi: '🇫🇮'
};

/**
 * Language names in their native language
 */
const LANGUAGE_NAMES = {
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  it: 'Italiano',
  es: 'Español',
  hu: 'Magyar',
  fi: 'Suomi'
};

/**
 * Language Switcher Component
 *
 * Displays current language with flag and provides dropdown to switch languages.
 * Only shows languages that are supported by the current merchant.
 */
const LanguageSwitcher = () => {
  const { t } = useTranslation();
  const { currentLanguage, supportedLanguages, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (lang) => {
    changeLanguage(lang);
    setIsOpen(false);
  };

  // Only show if merchant supports multiple languages
  if (supportedLanguages.length <= 1) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Current Language Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
        aria-label={t('manage.changeLanguage', 'Change language')}
      >
        <span className="text-xl" role="img" aria-label={LANGUAGE_NAMES[currentLanguage]}>
          {LANGUAGE_FLAGS[currentLanguage]}
        </span>
        <span className="text-sm font-medium text-gray-700 uppercase">
          {currentLanguage}
        </span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Language Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-300 rounded-lg shadow-lg z-50">
          <div className="py-1">
            {supportedLanguages.map((lang) => (
              <button
                key={lang}
                onClick={() => handleLanguageChange(lang)}
                className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-100 transition-colors ${
                  currentLanguage === lang ? 'bg-blue-50' : ''
                }`}
              >
                <span className="text-xl" role="img" aria-label={LANGUAGE_NAMES[lang]}>
                  {LANGUAGE_FLAGS[lang]}
                </span>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {LANGUAGE_NAMES[lang]}
                  </div>
                  <div className="text-xs text-gray-500 uppercase">
                    {lang}
                  </div>
                </div>
                {currentLanguage === lang && (
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
